import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TralenexLogo } from "@/components/brand/TralenexLogo";
import { useAuth } from "@/contexts/AuthContext";
import { IDPS } from "@/lib/mockData";
import { Check, Shield } from "lucide-react";

/**
 * S02 — SSO Redirect / Pending
 * Mock SSO bridge that simulates IdP roundtrip then mints a session.
 */
export default function SsoRedirect() {
  const { signIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("redirecting"); // redirecting -> verifying -> done

  const personaId = location.state?.personaId || "caio";
  const target = location.state?.target || "/dashboard";

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("verifying"), 700);
    const t2 = setTimeout(() => setPhase("done"), 1500);
    const t3 = setTimeout(() => {
      signIn(personaId);
      navigate(target, { replace: true });
    }, 1900);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [personaId, navigate, signIn, target]);

  return (
    <div className="min-h-screen w-full bg-canvas flex items-center justify-center px-6" data-testid="sso-redirect-page">
      <div className="w-full max-w-[420px] text-center anim-fade-up">
        <div className="flex justify-center mb-8">
          <TralenexLogo size={40} />
        </div>
        <h2 className="text-h1 text-ink-primary">Securing your session…</h2>
        <p className="mt-2 text-ink-secondary">
          Verifying with {IDPS[0].name}
        </p>

        <div className="mt-10 space-y-3 text-left mx-auto max-w-[320px]">
          <Step
            done={phase !== "redirecting"}
            label={`Redirecting to ${IDPS[0].name}`}
          />
          <Step
            done={phase === "done"}
            active={phase === "verifying"}
            label="MFA confirmed"
          />
          <Step
            done={phase === "done"}
            active={phase === "done"}
            label="Session signed and encrypted"
          />
        </div>

        <p className="mt-10 text-meta text-ink-secondary">
          ALGOS · A product of TRALENEX
        </p>
      </div>
    </div>
  );
}

function Step({ done, active, label }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-full border ${
          done
            ? "bg-tralenex-600 border-tralenex-600 text-white"
            : active
              ? "border-tralenex-600 text-tralenex-600"
              : "border-border-subtle text-ink-disabled"
        }`}
      >
        {done ? (
          <Check size={14} />
        ) : active ? (
          <span className="block h-1.5 w-1.5 rounded-full bg-tralenex-600 animate-pulse" />
        ) : (
          <span className="block h-1.5 w-1.5 rounded-full bg-ink-disabled" />
        )}
      </span>
      <span
        className={`text-sm ${done || active ? "text-ink-primary" : "text-ink-secondary"}`}
      >
        {label}
      </span>
    </div>
  );
}
